import type { MouseEvent as ReactMouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import Icon from 'components/priority/Icon/Icon'
import {
  cssColorToHex,
  editorTextBoxColorName,
  editorTextBoxSwatchRows,
  type EditorTextBoxThemeColors,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import * as S from './EditorTextBoxToolbar.styled'

type EditorTextBoxColorButtonProps = {
  /** The colour the selection paints; null while no selection is in the box. */
  color: string | null
  /** The selection covers runs of more than one colour. */
  mixed: boolean
  themeColors?: EditorTextBoxThemeColors
  open: boolean
  onMouseDown: (e: ReactMouseEvent<HTMLElement>) => void
}

/**
 * The toolbar's text colour button, right of the size stepper: an "A" over a
 * rainbow bar. Its tooltip reads "Text color" and, on a second line, the
 * current colour: "Mixed" over more than one, else the name of the tile that
 * holds it, else its hex.
 */
export const EditorTextBoxColorButton = ({
  color,
  mixed,
  themeColors,
  open,
  onMouseDown,
}: EditorTextBoxColorButtonProps) => {
  const { t } = useTranslation()
  const hex = cssColorToHex(color)
  const tile =
    hex === null
      ? undefined
      : editorTextBoxSwatchRows(themeColors).find(
          (swatch) => cssColorToHex(swatch.hex) === hex,
        )
  const current = mixed
    ? t('editor.textBox.toolbar.mixed')
    : tile
      ? editorTextBoxColorName(t, tile.name)
      : (hex?.toUpperCase() ?? color)
  const label = t('editor.textBox.toolbar.textColor')
  return (
    <S.Btn
      type="button"
      data-editor-text-color
      title={current ? `${label}\n${current}` : label}
      pressed={open}
      onMouseDown={onMouseDown}
    >
      <Icon icon="textColorRainbowIcon" width="16px" height="16px" />
    </S.Btn>
  )
}
